/**
 * Результат валидации. Единственный контракт для всех валидаторов.
 *
 * ИНВАРИАНТЫ:
 *   isValid === true  <=>  error === null
 *   isValid === false <=>  error === { message, code }
 *
 * Проверять результат ТОЛЬКО методами/свойствами класса:
 *   result.isValid     — булево свойство
 *   result.hasError()  — есть ли ошибка (контрактор)
 *   result.message     — текст ошибки или null
 *   result.code        — код ошибки или null
 *
 * Использование:
 *   ValidationResult.valid()                  — значение корректно
 *   ValidationResult.invalid('Некорректное ФИО', 2)
 *   ValidationResult.from(anyLegacyResult)    — нормализация старых валидаторов
 */
class ValidationResult {

	constructor(isValid, error = null) {
		this.isValid = isValid;
		// у валидного значения error всегда null, иначе — {message, code}
		this.error = isValid
			? null
			: (error ?? { message: 'Некорректное значение', code: null });
	}

	// контракторы (проверять результат только ими)
	hasError() {
		return this.error !== null;
	}

	get message() {
		return this.error?.message ?? null;
	}

	get code() {
		return this.error?.code ?? null;
	}

	// ── фабрики ──
	static valid() {
		return new ValidationResult(true);
	}

	static invalid(message, code = null) {
		return new ValidationResult(false, {
			message: String(message ?? 'Некорректное значение'),
			code: code ?? null,
		});
	}

	/**
	 * Нормализация ЛЮБОГО результата к контракту класса.
	 */
	static from(raw) {
		if (raw instanceof ValidationResult) {
			return raw;
		}
		if (raw === true) {
			return ValidationResult.valid();
		}
		if (raw === false) {
			return ValidationResult.invalid('Некорректное значение');
		}
		if (typeof raw === 'string') {
			return ValidationResult.invalid(raw);
		}
		if (raw == null) {
			return ValidationResult.valid();
		}

		const err = raw.error;
		const errFilled = err && typeof err === 'object' && Object.keys(err).length > 0;
		const failed = raw.isValid === false || errFilled;

		if (!failed) {
			return ValidationResult.valid();
		}
		return new ValidationResult(false, {
			message: err?.message ?? 'Некорректное значение',
			code: err?.code ?? null,
		});
	}
}

window.ValidationResult = ValidationResult;
